import { useState, useEffect } from "react";
import { Plus, Trash2 } from "lucide-react";
import { FiEye, FiPlusCircle } from "react-icons/fi";
import { useInteriorDesign } from "./useHook.js";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import GlobalPagination from "../../Components/Pagination/GlobalPagination";
import InteriorDesignModal from "../../Components/Modals/InteriorDesign/InteriorDesignModal.jsx";
import AddPopularInteriorDesign from "../../Components/Modals/PopularInteriorDesign/PopularInteriorDesign";
import SimilarInteriorDesignModal from "../../Components/Modals/SimilarInteriorDesign/SimilarInteriorDesign";
import Loader from "../../Utils/loader";

const InteriorDesign = () => {
  const navigate = useNavigate();
  const {
    getInteriorDesign,
    createInteriorDesign,
    updateInteriorDesign,
    deleteInteriorDesign,
  } = useInteriorDesign();

  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [interiorDesignData, setInteriorDesignData] = useState([]);
  const [offset, setOffset] = useState(0);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [showPopularDesignModal, setShowPopularDesignModal] = useState(false);
  const [isSimilarDesignModalOpen, setIsSimilarDesignModalOpen] = useState(false);
  const [searchFilter, setSearchFilter] = useState("");
  const [categoryName, setCategoryName] = useState("");

  useEffect(() => {
    loadInteriorDesigns(0, limit);
  }, []);

  const handlePageChange = (newOffset) => {
    setOffset(newOffset);
    loadInteriorDesigns(newOffset, limit);
  };

  const loadInteriorDesigns = async (currentOffset = offset, currentLimit = limit) => {
    setIsLoading(true);
    try {
      await getInteriorDesign(setInteriorDesignData, setTotal, currentOffset, currentLimit, searchFilter, categoryName);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load interior designs. Please refresh the page.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (formData) => {
    setIsSubmitting(true);

    try {
      const apiData = new FormData();

      Object.keys(formData).forEach((key) => {
        if (key !== "images" && key !== "imagePreviews" && key !== "primaryImageIndex") {
          if (key === "features") {
            apiData.append(key, JSON.stringify(formData[key]));
          } else {
            apiData.append(key, formData[key]);
          }
        }
      });

      if (formData.images && formData.images.length > 0) {
        formData.images.forEach((image, index) => {
          apiData.append(`image`, image);
          const isPrimary = index === formData.primaryImageIndex;
          apiData.append(`isPrimary`, isPrimary);
        });
      }

      if (isEditing && formData.existingImages) {
        formData.existingImages.forEach((image) => {
          apiData.append("existing_images", JSON.stringify(image));
        });
      }

      let response;
      if (isEditing) {
        response = await updateInteriorDesign(editId, apiData);
      } else {
        response = await createInteriorDesign(apiData);
      }

      await loadInteriorDesigns(offset, limit);

      setShowModal(false);
      setIsEditing(false);
      setEditId(null);
      setEditData(null);

      Swal.fire({
        icon: "success",
        title: isEditing ? "Interior Design Updated" : "Interior Design Added",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (error) {
      console.error("Failed to submit form:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to save interior design. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = async (id) => {
    try {
      const design = interiorDesignData.find(item => item.id === id);
      if (design) {
        const transformedDesign = {
          ...design,
          existingImages: design.images || []
        };
        setEditData(transformedDesign);
        setIsEditing(true);
        setEditId(id);
        setShowModal(true);
      }
    } catch (error) {
      console.error("Failed to load design for editing:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load design for editing. Please try again.",
      });
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      try {
        await deleteInteriorDesign(id);
        await loadInteriorDesigns(offset, limit);
        Swal.fire("Deleted!", "Interior design deleted successfully.", "success");
      } catch (error) {
        console.error("Failed to delete interior design:", error);
        Swal.fire(
          "Error!",
          "Failed to delete interior design. Please try again.",
          "error"
        );
      }
    }
  };

  const handlePageView = (id) => {
    navigate(`/interior-design/${id}`);
  };

  const handleAddNew = () => {
    setIsEditing(false);
    setEditId(null);
    setEditData(null);
    setShowModal(true);
  };

  const handleAddSimilarDesign = (id) => {
    setIsSimilarDesignModalOpen(id);
  };

  const handleSearch = async () => {
    setIsLoading(true);
    setOffset(0);
    try {
      await getInteriorDesign(setInteriorDesignData, setTotal, 0, limit, searchFilter, categoryName);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to search interior designs. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setSearchFilter("");
    setCategoryName("");
    setOffset(0);
    getInteriorDesign(setInteriorDesignData, setTotal, 0, limit, "", "");
  };

  if (isLoading) {
    return <Loader message="Loading interior designs..." />;
  }

  return (
    <div className="bg-gray-100">
      {/* Content */}
      <div className="p-2">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold">Interior Design</h3>
          <div className="space-x-2">
            <button
              className="bg-blue-500 text-white px-4 py-2 rounded-md"
              onClick={handleAddNew}
            >
              Add Interior Design
            </button>
            <button
              className="bg-blue-500 text-white px-4 py-2 rounded-md"
              onClick={() => setShowPopularDesignModal(true)}
            >
              Add Popular Design
            </button>
          </div>
        </div>

        <div className="mt-4">
          <div className="bg-white p-4 rounded-md shadow-md mb-6 flex gap-4 mt-4">
            <input
              type="text"
              className="w-[40%] flex-grow px-4 py-2 border rounded-lg"
              placeholder="Search by name"
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
            />
            <input
              type="text"
              className="w-[40%] flex-grow px-4 py-2 border rounded-lg"
              placeholder="Search by category name"
              value={categoryName}
              onChange={(e) => setCategoryName(e.target.value)}
            />
            <button
              className="w-full sm:w-[45%] md:w-60 bg-blue-500 text-white px-4 py-2 rounded-md"
              onClick={handleSearch}
            >
              Search
            </button>
            <button
              className="w-full sm:w-[45%] md:w-60 bg-primary-dark text-white px-4 py-2 rounded-md"
              onClick={handleReset}
            >
              Reset
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-md shadow-md overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-gradient-to-r from-primary-dark to-primary-light text-white">
                <th className="py-3 px-4 text-left">Sr. No.</th>
                <th className="py-3 px-4 text-left">Title</th>
                <th className="py-3 px-4 text-left">Room Size</th>
                <th className="py-3 px-4 text-left">Category</th>
                {/* <th className="py-3 px-4 text-left">Design Type</th> */}
                <th className="py-3 px-4 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {interiorDesignData.length > 0 ? (
                interiorDesignData.map((design, index) => (
                  <tr key={design.id} className="border-b">
                    <td className="py-3 px-4">{offset + index + 1}</td>
                    <td className="py-3 px-4">{design.title}</td>
                    <td className="py-3 px-4">{design.room_size}</td>
                    <td className="py-3 px-4">{design.category?.name}</td>
                    {/* <td className="py-3 px-4">{design.design_type}</td> */}
                    <td className="py-3 px-4">
                      <div className="flex space-x-2">
                        <button
                          className="bg-green-500 text-white p-2 rounded-md flex items-center gap-1"
                          onClick={() => handleAddSimilarDesign(design.id)}
                        >
                          <FiPlusCircle size={16} />
                          Similar Design
                        </button>
                        <button
                          className="bg-red-500 text-white p-2 rounded-md"
                          onClick={() => handleDelete(design.id)}
                        >
                          <Trash2 size={16} />
                        </button>
                        <button
                          className="bg-blue-500 text-white p-2 rounded-md flex items-center gap-1"
                          onClick={() => handlePageView(design.id)}
                        >
                          <FiEye size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="9"
                    className="py-4 px-4 text-center text-gray-500 font-bold"
                  >
                    No interior designs found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {interiorDesignData.length > 0 && total > limit && (
          <div className="mt-4">
            <GlobalPagination
              offset={offset}
              limit={limit}
              total={total}
              onPageChange={handlePageChange}
              className="bg-white"
            />
          </div>
        )}
      </div>

      {/* Interior Design Modal */}
      <InteriorDesignModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setIsEditing(false);
          setEditId(null);
          setEditData(null);
        }}
        onSubmit={handleSubmit}
        isEditing={isEditing}
        editData={editData}
        isSubmitting={isSubmitting}
      />

      {/* Add Popular Interior Design Modal */}
      {showPopularDesignModal && (
        <AddPopularInteriorDesign
          isOpen={showPopularDesignModal}
          onClose={() => setShowPopularDesignModal(false)}
        />
      )}

      {/* Similar Interior Design Modal */}
      {isSimilarDesignModalOpen && (
        <SimilarInteriorDesignModal
          isOpen={isSimilarDesignModalOpen}
          onClose={() => setIsSimilarDesignModalOpen(false)}
          designId={isSimilarDesignModalOpen}
        />
      )}
    </div>
  );
};

export default InteriorDesign;